/* LLCL Campaign Announcement / Split-Flap V1.0
 * Phase rules in Liquid; only prelaunch numerals use mechanical JS.
 * No third-party dependencies; prevents duplicate managers in Theme Editor.
 */
(() => {
  'use strict';
  const KEY = 'LLCampaignAnnouncementFlap';
  const ROOT = '[data-ll-campaign-bar]';
  if (window[KEY]) {
    window[KEY].scan(document);
    return;
  }
  const instances = new Map();
  const prefersReduced = () => Boolean(window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const pad = value => String(Math.max(0, value)).padStart(2, '0').slice(-2);

  function half(side, extra, value) {
    const node = document.createElement('span');
    node.className = 'll-ca-flap__half ll-ca-flap__half--' + side + (extra ? ' ' + extra : '');
    const glyph = document.createElement('span');
    glyph.className = 'll-ca-flap__glyph';
    glyph.textContent = value;
    node.append(glyph);
    return node;
  }

  function createCell(value) {
    const cell = document.createElement('span');
    cell.className = 'll-ca-flap__cell';
    cell.dataset.current = value;
    cell.setAttribute('aria-hidden', 'true');
    cell.append(
      half('top', '', value),
      half('bottom', '', value),
      half('top', 'll-ca-flap__top-flip', value),
      half('bottom', 'll-ca-flap__bottom-flip', value)
    );
    const seam = document.createElement('span');
    seam.className = 'll-ca-flap__seam';
    cell.append(seam);
    return cell;
  }

  function setDigit(cell, next, animate) {
    if (cell.dataset.current === next && !cell.classList.contains('is-flipping')) return;
    if (cell._flipTimer) {
      clearTimeout(cell._flipTimer);
      cell._flipTimer = null;
    }
    cell.classList.remove('is-flipping');

    const glyphs = cell.querySelectorAll('.ll-ca-flap__glyph');
    if (!animate || prefersReduced()) {
      glyphs.forEach(node => { node.textContent = next; });
      cell.dataset.current = next;
      return;
    }
    const previous = cell.dataset.current || '0';
    glyphs[0].textContent = next;
    glyphs[1].textContent = previous;
    glyphs[2].textContent = previous;
    glyphs[3].textContent = next;
    cell.dataset.current = next;
    void cell.offsetWidth;
    cell.classList.add('is-flipping');
    cell._flipTimer = setTimeout(() => {
      glyphs.forEach(node => { node.textContent = next; });
      cell.classList.remove('is-flipping');
      cell._flipTimer = null;
    }, 395);
  }

  class Countdown {
    constructor(root) {
      this.root = root;
      this.preview = root.dataset.preview || 'auto';
      this.language = root.dataset.language === 'en' ? 'en' : 'es';
      this.launch = Date.parse(root.dataset.launch);
      this.end = Date.parse(root.dataset.end);
      this.started = Date.now();
      this.fakeStart = this.launch - 570600000;
      this.prelaunch = root.querySelector('[data-ll-campaign-prelaunch]');
      this.live = root.querySelector('[data-ll-campaign-live]');
      this.board = root.querySelector('[data-ll-campaign-flap]');
      this.groups = new Map();
      this.lastMinute = null;
      this.clock = null;
      this.onVisibility = this.onVisibility.bind(this);
      this.tick = this.tick.bind(this);
      if (root.dataset.phase === 'prelaunch' && this.board) {
        this.hydrate();
        this.tick(false);
        this.clock = setInterval(this.tick, 1000);
      } else if (root.dataset.phase === 'live' && this.preview === 'auto' && Number.isFinite(this.end)) {
        // Re-check the expiry without animating or constantly updating the DOM.
        this.clock = setInterval(this.tick, 30000);
      }
      document.addEventListener('visibilitychange', this.onVisibility);
    }

    hydrate() {
      for (const name of ['days', 'hours', 'minutes']) {
        const host = this.board.querySelector('[data-ll-flap-field="' + name + '"]');
        if (!host) continue;
        const digits = (host.textContent || '00').trim().padStart(2, '0').slice(-2);
        const cells = [createCell(digits[0]), createCell(digits[1])];
        host.replaceChildren(...cells);
        host.classList.add('is-ready');
        this.groups.set(name, cells);
      }
    }

    onVisibility() {
      if (!document.hidden) this.tick(false);
    }

    currentTime() {
      if (this.preview === 'prelaunch') {
        return this.fakeStart + (Date.now() - this.started);
      }
      return Date.now();
    }

    tick(animate = true) {
      if (!this.root.isConnected || !Number.isFinite(this.launch)) {
        this.destroy();
        return;
      }
      if (document.hidden) return;
      const now = this.currentTime();
      const remaining = this.launch - now;
      if (this.preview === 'auto' && now >= this.end) {
        this.root.hidden = true;
        this.root.dataset.phase = 'fallback';
        this.destroy();
        return;
      }
      if (this.preview === 'auto' && now >= this.launch) {
        if (this.root.dataset.phase !== 'live') {
          if (this.prelaunch) this.prelaunch.hidden = true;
          if (this.live) this.live.hidden = false;
          this.root.dataset.phase = 'live';
        }
        return;
      }
      if (this.root.dataset.phase !== 'prelaunch') return;
      const totalMinutes = Math.max(0, Math.ceil(remaining / 60000));
      if (totalMinutes === this.lastMinute) return;
      const days = Math.floor(totalMinutes / 1440);
      const hours = Math.floor((totalMinutes % 1440) / 60);
      const minutes = totalMinutes % 60;
      for (const [name, amount] of [['days', days], ['hours', hours], ['minutes', minutes]]) {
        const cells = this.groups.get(name);
        if (!cells) continue;
        const digits = pad(amount);
        setDigit(cells[0], digits[0], animate);
        setDigit(cells[1], digits[1], animate);
      }
      if (this.board) {
        const label = this.language === 'en'
          ? days + ' days, ' + hours + ' hours and ' + minutes + ' minutes until launch'
          : 'Faltan ' + days + ' días, ' + hours + ' horas y ' + minutes + ' minutos para el lanzamiento';
        this.board.setAttribute('aria-label', label);
      }
      this.lastMinute = totalMinutes;
    }

    destroy() {
      if (this.clock) clearInterval(this.clock);
      this.clock = null;
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.groups.forEach(cells => {
        cells.forEach(cell => {
          if (cell._flipTimer) clearTimeout(cell._flipTimer);
        });
      });
      this.groups.clear();
    }
  }

  function prune() {
    instances.forEach((item, root) => {
      if (!root.isConnected) {
        item.destroy();
        instances.delete(root);
      }
    });
  }

  function scan(context = document) {
    prune();
    const init = root => {
      if (!instances.has(root)) instances.set(root, new Countdown(root));
    };
    if (context.matches && context.matches(ROOT)) init(context);
    if (context.querySelectorAll) context.querySelectorAll(ROOT).forEach(init);
  }

  function onUnload(event) {
    instances.forEach((item, root) => {
      if (root === event.target || event.target.contains(root)) {
        item.destroy();
        instances.delete(root);
      }
    });
  }

  const onLoad = event => scan(event.target);
  document.addEventListener('shopify:section:load', onLoad);
  document.addEventListener('shopify:section:unload', onUnload);
  window[KEY] = {
    version: '1.0',
    scan,
    destroy() {
      instances.forEach(item => item.destroy());
      instances.clear();
      document.removeEventListener('shopify:section:load', onLoad);
      document.removeEventListener('shopify:section:unload', onUnload);
      delete window[KEY];
    }
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(document), {once:true});
  } else {
    scan(document);
  }
})();